// src/lib/operateur-acquiescement.js — QUAND « OUI » VEUT DIRE « CLIQUE ».
//
// ══ POURQUOI (décision Dimitri, 2026-09-15) ════════════════════════════════
//
//   « Aujourd'hui il décrit le levier, il ne l'appelle pas. Le "oui" doit
//     devenir le clic. » Et : « il propose, je valide ; qu'il arrête de me
//     faire répéter le contexte et qu'il aille au bout du geste quand je dis
//     oui. »
//
// La carte était déjà là, le bouton aussi. Ce qui manquait, c'est que le mot
// « oui », tapé dans le champ de conversation, vaille le clic sur ce bouton.
//
// ══ CE MODULE NE DÉCIDE RIEN D'AUTRE ═══════════════════════════════════════
//
// ⚠️ IL N'ÉLARGIT AUCUN POUVOIR. Il ne dit PAS s'il faut agir : il dit
// seulement si la phrase de Dimitri est un assentiment NU. L'exécution passe
// ensuite par exactement le même chemin que le clic — la carte, ses gardes
// (motif, choix, aperçu, vague), `executerGeste`, son jeton. Une phrase que ce
// module refuse retombe sur le comportement d'avant : elle part au modèle.
//
// ⚠️ ET C'EST POURQUOI IL EST AUSSI ÉTROIT. « oui » sur un geste d'argent
// engage autant qu'un clic, sans l'avoir relu. Le doute se résout donc TOUJOURS
// vers `null` : au pire Dimitri clique le bouton, ce qu'il faisait hier.
//
// ⚠️ ET IL NE LIT PAS DES MOTS, IL LIT DES FORMES (corrigé le 2026-09-15, revue
// adverse de #1432). Un « sac de mots » — « tous les mots de la phrase sont-ils
// dans la liste ? » — acceptait les recombinaisons de leurs morceaux : « bon »,
// « y va », « sur le bon » lançaient un geste. On compare la PHRASE ENTIÈRE à
// une liste fermée d'expressions, et une question (« ok ? ») n'est jamais un
// accord.
//
// ⚠️ MODULE PUR — aucun `import`, aucune horloge, aucune lecture. La liste des
// verbes d'action est RECOPIÉE de `lib/operateur-pieges.js` (CRA refuse les
// imports hors de `src/`), et l'égalité est verrouillée par un test de parité.

// ── LES VERBES D'ACTION, EN FORME DE COMMANDE ──────────────────────────────
// ⚠️ MIROIR de `lib/operateur-pieges.js::VERBES`. Ne se modifie que là-bas, et
// se recopie ici — `src/lib/operateur-acquiescement.test.js` fait échouer la
// divergence. Leur rôle ici est inverse de là-bas : là-bas ils reconnaissent un
// ORDRE, ici leur seule présence prouve que la phrase n'est PAS un « oui » nu.
export const VERBES = [
  "baisse",
  "monte",
  "augmente",
  "publie",
  "depublie",
  "dépublie",
  "supprime",
  "efface",
  "envoie",
  "ecris",
  "écris",
  "relance",
  "bloque",
  "debloque",
  "débloque",
  "assigne",
  "preleve",
  "prélève",
  "rembourse",
  "capture",
  "libere",
  "libère",
  "change",
  "modifie",
  "applique",
  "active",
  "desactive",
  "désactive",
  "qualifie",
  "corrige",
  "ajoute",
  "retire",
  "mets",
  "fixe",
  "annule",
  "valide",
  "refuse",
  "coupe",
  "deplace",
  "déplace",
  "verrouille",
  "deverrouille",
  "déverrouille",
  "reordonne",
  "réordonne",
  "remplace",
  "planifie",
  // ── LES VERBES DE CRÉATION (2026-09-15, vague 10 « les formulaires ») ────
  // ⚠️ SANS EUX, LA NÉGATION NE MORDAIT PAS SUR CE QUE LA VAGUE 10 INTRODUIT.
  // « mais ne l'inscris pas tout de suite » ne contenait aucun verbe de cette
  // liste : la garde des pièges laissait donc passer la carte, sur le seul
  // registre de gestes qui fait EXISTER quelque chose. Le geste qu'on annule
  // le moins facilement est celui qui crée. Côté acquiescement, ils évitent
  // aussi qu'un « inscris-le » soit lu comme un « oui ».
  "inscris",
  "cree",
  "crée",
  "enregistre",
];

/** Sans accents, en minuscules — tout le module travaille sur cette forme. */
function normaliser(t) {
  return String(t || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

const VERBES_N = new Set(VERBES.map(normaliser));

// Les mots qui ne portent rien : on les retire avant de compter. « oui s'il te
// plaît » doit valoir « oui ».
const POLITESSE = new Set([
  "s",
  "il",
  "te",
  "vous",
  "plait",
  "stp",
  "svp",
  "merci",
  "alors",
  "donc",
  "du",
  "coup",
  "bah",
  "ben",
  "euh",
]);

// ── CE QUI LANCE — DES FORMES ENTIÈRES, JAMAIS DES MOTS ────────────────────
//
// ⚠️ CORRIGÉ LE 2026-09-15, revue adverse de #1432 (MAJEUR 2). La première
// version lisait un SAC DE MOTS : « tous les mots de la phrase sont-ils dans la
// liste ? ». Comme la liste devait contenir les MORCEAUX des expressions
// (« c », « est », « bon » pour « c'est bon » ; « le » pour « fais-le » ;
// « y » pour « vas-y »), elle acceptait leurs recombinaisons : « bon »,
// « y va », « sur le bon », « le » — un mot isolé lançait un geste.
//
// Un sac de mots n'a pas de sens ; une FORME en a un. On compare donc la phrase
// entière, normalisée, à une liste fermée d'expressions. Ce qui n'y est pas
// exactement repart au modèle — c'est-à-dire au comportement d'hier.
const LANCE = new Set([
  "oui",
  "ouais",
  "ouai",
  "ok",
  "okay",
  "yes",
  "yep",
  "d accord",
  "daccord",
  "oui d accord",
  "ok d accord",
  "vas y",
  "va y",
  "allez y",
  "on y va",
  "oui vas y",
  "ok vas y",
  "go",
  "ok go",
  "oui go",
  "c est parti",
  "lance",
  "lance le",
  "oui lance",
  "fais le",
  "oui fais le",
  "ok fais le",
  "c est bon",
  "oui c est bon",
  "ok c est bon",
  "parfait",
  "oui parfait",
  "exact",
  "bien sur",
  "oui ok",
  "ok oui",
  "oui oui",
]);

// ── CE QUI ANNULE ──────────────────────────────────────────────────────────
// Mêmes règles : des formes entières.
// ⚠️ « annule » est AUSSI un verbe d'action (`VERBES`). C'est la seule
// intersection, et elle est voulue : ici la phrase porte sur la carte en
// attente, pas sur une nouvelle action. Elle est donc lue AVANT la garde des
// verbes — et « annule la réservation 4712 » retombe à `null` (ce n'est pas une
// forme connue, et son chiffre suffirait à la refuser).
const ANNULE = new Set([
  "non",
  "nan",
  "nope",
  "non non",
  "annule",
  "annule le",
  "annule ca",
  "laisse tomber",
  "laisse",
  "attends",
  "attend",
  "attendez",
  "stop",
  "oublie",
  "oublie ca",
  "pas maintenant",
  "pas tout de suite",
  "surtout pas",
]);

// Ce qui NUANCE : dès qu'un de ces mots apparaît, la phrase n'est plus un
// assentiment nu — elle corrige, elle restreint, elle demande autre chose.
const NUANCES = new Set([
  "mais",
  "plutot",
  "sauf",
  "contre",
  "si",
  "juste",
  "seulement",
  "avant",
  "apres",
  "sans",
]);

// La négation, hors des formes d'annulation reconnues ci-dessus.
const NEGATIONS = new Set(["ne", "n", "jamais", "plus", "rien", "aucun"]);

// Un assentiment tient en quelques mots. Au-delà, c'est une phrase — et une
// phrase mérite le modèle, pas un clic. (Les formes ci-dessus sont toutes plus
// courtes : cette borne ne sert qu'à refuser TÔT, avec un motif lisible.)
const MAX_MOTS = 5;

/**
 * Lit l'assentiment d'une phrase.
 *
 * @param {string} phrase — ce que Dimitri vient d'écrire, tel quel
 * @returns {{verdict:'lance'|'annule'|null, motif:string}}
 *   `motif` dit POURQUOI on n'a pas tranché — utile aux tests et à la trace,
 *   jamais affiché tel quel.
 */
export function lireAcquiescement(phrase) {
  const texte = String(phrase || "");

  // ── 0 · UNE QUESTION N'EST PAS UN ACCORD ─────────────────────────────────
  // ⚠️ AVANT TOUT NETTOYAGE (revue adverse #1432). La ponctuation était effacée
  // en tête de traitement, si bien que « ok ? » — qui DEMANDE une confirmation
  // — devenait « ok » et lançait le geste. Un doute exprimé ne peut pas valoir
  // un consentement.
  if (/[?¿]/.test(texte)) return { verdict: null, motif: "question" };

  const brut = normaliser(texte)
    // L'apostrophe sépare ; le reste de la ponctuation disparaît. On GARDE les
    // chiffres et l'euro : ils servent à refuser, pas à découper.
    .replace(/['\u2019]/g, " ")
    .replace(/[^a-z0-9@_\u20ac\s-]/g, " ");
  const mots = brut
    .split(/[\s-]+/)
    .filter(Boolean)
    .filter((m) => !POLITESSE.has(m));

  if (!mots.length) return { verdict: null, motif: "vide" };
  if (mots.length > MAX_MOTS) return { verdict: null, motif: "trop long" };

  // ⚠️ UN CHIFFRE, UN MONTANT, UN IDENTIFIANT ⇒ JAMAIS. « oui, 40 € » n'est pas
  // un assentiment : c'est une nouvelle instruction, et le chiffre qu'elle
  // porte n'a été relu sur AUCUNE carte.
  for (const m of mots)
    if (/[0-9@_\u20ac]/.test(m)) return { verdict: null, motif: "chiffre" };

  for (const m of mots)
    if (NUANCES.has(m)) return { verdict: null, motif: "nuance" };

  // LA FORME ENTIÈRE, jamais mot à mot.
  const forme = mots.join(" ");

  // L'annulation AVANT la garde des verbes : « annule » est l'un d'eux.
  if (ANNULE.has(forme)) return { verdict: "annule", motif: "annulation" };

  for (const m of mots)
    if (VERBES_N.has(m)) return { verdict: null, motif: "verbe d'action" };
  for (const m of mots)
    if (NEGATIONS.has(m)) return { verdict: null, motif: "negation" };

  if (LANCE.has(forme)) return { verdict: "lance", motif: "assentiment" };

  return { verdict: null, motif: "autre chose" };
}

export const _internal = { VERBES_N, LANCE, ANNULE, NUANCES, MAX_MOTS };
